import React from 'react';
import { Link } from 'react-router-dom';
import { getInitials, stringToColor } from '../../../utils/string/nameUtils';
import currencyHelper from '../../../helpers/currencyHelper';
import type { LeaderboardListItemProps } from './types';
import styles from './LeaderboardListItem.module.css';

/**
 * Unified leaderboard list item component
 * Can render fundraisers, donations, teams, collections, etc.
 * Uses FundraiserListItem's superior visual design as the base
 */
export function LeaderboardListItem<T>({
  item,
  rank = 0,
  showRank = true,
  config,
  onClick,
}: LeaderboardListItemProps<T>) {
  const {
    getId,
    getName,
    getAmount,
    getMetadata,
    getLink,
    getCurrency,
    getImageUrl,
    showAvatar = true,
    fullRowClickable = true,
  } = config;

  // Extract data using config functions
  const itemId = getId(item);
  const displayName = getName(item);
  const amount = getAmount(item);
  const currency = getCurrency(item);
  const metadata = getMetadata(item);
  const link = getLink(item);
  const imageUrl = getImageUrl?.(item);

  const handleClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const getRankClass = () => {
    if (!showRank) return styles.rankBadgeOther;
    if (rank === 1) return styles.rankBadgeGold;
    if (rank === 2) return styles.rankBadgeSilver;
    if (rank === 3) return styles.rankBadgeBronze;
    return styles.rankBadgeOther;
  };

  // Render avatar/thumbnail (image or initials)
  const renderAvatar = () => {
    if (!showAvatar) return null;

    if (imageUrl) {
      // Image thumbnail with squared border-radius (8px)
      const imageSrc = `${imageUrl}?w=32&h=32`;
      return (
        <div className={styles.thumbnail}>
          <img src={imageSrc} alt="" className={styles.thumbnailImage} />
        </div>
      );
    }

    // Initials avatar with colored background
    const initials = getInitials(displayName);
    const backgroundColor = stringToColor(displayName);

    return (
      <div
        className={styles.thumbnail}
        style={{ backgroundColor }}
      >
        <span className={styles.thumbnailInitials}>{initials}</span>
      </div>
    );
  };

  // Content inside the link
  const linkContent = (
    <div className={styles.leaderboardItemLeft}>
      {showRank && (
        <div className={`${styles.rankBadge} ${getRankClass()}`}>
          {rank}
        </div>
      )}
      {renderAvatar()}
      <div className={styles.leaderboardItemInfo}>
        <div className={styles.leaderboardItemName}>
          {displayName}
        </div>
        {metadata && (
          <div className={styles.leaderboardItemMeta}>
            {metadata}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.leaderboardItem}>
      {fullRowClickable ? (
        <Link
          to={link}
          className={styles.leaderboardItemLink}
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          {linkContent}
        </Link>
      ) : (
        linkContent
      )}
      <div className={styles.leaderboardItemAmount}>
        {currencyHelper.formatCurrency(amount, currency)}
      </div>
    </div>
  );
}
